import { api, ulid } from '@/shared/api';
import { unwrap, unwrapCollection } from '@/shared/api/envelope';

import { toPriceList, type PriceList } from './platform-model';

import {
    toBranding,
    toMailSettings,
    toMailTestResult,
    toTenant,
    toTenantDomain,
    toTenantSettings,
    type Branding,
    type CompanyDetails,
    type DomainType,
    type MailSettings,
    type MailSettingsInput,
    type MailTestResult,
    type Tenant,
    type TenantDomain,
    type TenantSettings,
    type TenantSettingsPatch,
} from './model';
import {
    mailDeliveryResourceSchema,
    mailSettingsResourceSchema,
    pricingResourceSchema,
    tenantBrandingResourceSchema,
    tenantDomainResourceSchema,
    tenantResourceSchema,
    tenantSettingsResourceSchema,
    uploadedMediaResourceSchema,
} from './types';

/** Schrijfacties gaan nooit in de offline cache. */
const WRITE = { cacheKey: false } as const;

/**
 * De eigen omgeving (`02-api-contract.md` §Tenant & instellingen). Alles onder
 * `/tenant` volgt de omgeving van de sessie; er gaat nooit een bedrijf in het pad.
 * Een domein heet in het pad bij zijn `uuid`.
 */
export const tenantApi = {
    async current(): Promise<Tenant> {
        return toTenant(unwrap(tenantResourceSchema, await api.get('/tenant')));
    },

    /**
     * De prijzenlijst zoals de website hem toont (`GET /public/pricing`): de
     * abonnementen in hun volgorde, de proef, een extra keuring en een losse
     * rapportage.
     */
    async pricing(): Promise<PriceList> {
        return toPriceList(unwrap(pricingResourceSchema, await api.get('/public/pricing')));
    },

    /** De bedrijfsgegevens, allemaal tegelijk. Een leeg veld haalt het gegeven weg. */
    async updateDetails(details: CompanyDetails): Promise<Tenant> {
        const text = (value: string) => (value.trim() === '' ? null : value.trim());

        return toTenant(
            unwrap(
                tenantResourceSchema,
                await api.patch(
                    '/tenant',
                    {
                        name: details.name.trim(),
                        legal_name: text(details.legalName),
                        kvk_number: text(details.kvkNumber),
                        vat_number: text(details.vatNumber),
                        iban: text(details.iban),
                        email: text(details.email),
                        phone: text(details.phone),
                        website: text(details.website),
                        address_street: details.address.street,
                        address_number: details.address.number,
                        address_suffix: details.address.suffix,
                        address_postcode: details.address.postcode,
                        address_city: details.address.city,
                    },
                    WRITE,
                ),
            ),
        );
    },

    async branding(): Promise<Branding> {
        return toBranding(unwrap(tenantBrandingResourceSchema, await api.get('/tenant/branding')));
    },

    /**
     * De huisstijl: kleuren (zes tekens, `#1F6FEB`) of een logo.
     *
     * Wat je niet meestuurt, blijft staan. `null` haalt weg: bij het logo het
     * bestand, bij de twee kleuren naast het accent de eigen keuze — de API
     * kiest dan weer zelf wat leesbaar is.
     */
    async updateBranding(input: {
        accentColor?: string;
        accentTextColor?: string | null;
        headingColor?: string | null;
        logoMediaUuid?: string | null;
    }): Promise<Branding> {
        return toBranding(
            unwrap(
                tenantBrandingResourceSchema,
                await api.patch(
                    '/tenant/branding',
                    {
                        ...(input.accentColor === undefined
                            ? {}
                            : { accent_color: input.accentColor }),
                        ...(input.accentTextColor === undefined
                            ? {}
                            : { accent_text_color: input.accentTextColor }),
                        ...(input.headingColor === undefined
                            ? {}
                            : { heading_color: input.headingColor }),
                        ...(input.logoMediaUuid === undefined
                            ? {}
                            : { logo_media_uuid: input.logoMediaUuid }),
                    },
                    WRITE,
                ),
            ),
        );
    },

    /**
     * Een nieuw logo: eerst het bestand naar `POST /media` (collectie `logo`),
     * dan dat bestand kiezen in de huisstijl. De API bepaalt wat een logo mag
     * zijn; een ander formaat of een te groot bestand is een 415 of 413.
     */
    async uploadLogo(file: File): Promise<Branding> {
        const body = new FormData();

        body.append('file', file);
        body.append('client_uuid', ulid());
        body.append('collection', 'logo');

        const media = unwrap(
            uploadedMediaResourceSchema,
            await api.post('/media', body, { ...WRITE, timeoutMs: 60_000 }),
        );

        return tenantApi.updateBranding({ logoMediaUuid: media.uuid });
    },

    async settings(): Promise<TenantSettings> {
        return toTenantSettings(
            unwrap(tenantSettingsResourceSchema, await api.get('/tenant/settings')),
        );
    },

    /** Partieel: wat je niet stuurt, blijft staan. */
    async updateSettings(patch: TenantSettingsPatch): Promise<TenantSettings> {
        return toTenantSettings(
            unwrap(tenantSettingsResourceSchema, await api.patch('/tenant/settings', patch, WRITE)),
        );
    },

    /** Alleen de eigenaar: er staan inloggegevens in. */
    async mailSettings(): Promise<MailSettings> {
        return toMailSettings(
            unwrap(mailSettingsResourceSchema, await api.get('/tenant/mail-settings', WRITE)),
        );
    },

    async updateMailSettings(input: MailSettingsInput): Promise<MailSettings> {
        return toMailSettings(
            unwrap(
                mailSettingsResourceSchema,
                await api.patch(
                    '/tenant/mail-settings',
                    {
                        driver: input.driver,
                        host: input.host,
                        port: input.port,
                        username: input.username,
                        encryption: input.encryption,
                        from_name: input.fromName,
                        from_address: input.fromAddress,
                        reply_to_address: input.replyToAddress,
                        ...(input.password ? { password: input.password } : {}),
                    },
                    WRITE,
                ),
            ),
        );
    },

    /**
     * Een testbericht via de ingestelde server. Het antwoord is altijd 200: een
     * weigerende mailserver is een uitkomst, geen fout in het verzoek.
     */
    async sendTestMail(recipient?: string): Promise<MailTestResult> {
        return toMailTestResult(
            unwrap(
                mailDeliveryResourceSchema,
                await api.post('/tenant/mail-settings/test', recipient ? { recipient } : {}, {
                    ...WRITE,
                    timeoutMs: 60_000,
                }),
            ),
        );
    },

    async domains(): Promise<TenantDomain[]> {
        return unwrapCollection(
            tenantDomainResourceSchema,
            await api.get('/tenant/domains'),
        ).items.map(toTenantDomain);
    },

    async addDomain(input: { hostname: string; type: DomainType }): Promise<TenantDomain> {
        return toTenantDomain(
            unwrap(tenantDomainResourceSchema, await api.post('/tenant/domains', input, WRITE)),
        );
    },

    /** Zoekt het TXT-record of het bestand op de website; mislukt is een 422 met wat er wél stond. */
    async verifyDomain(domainUuid: string): Promise<TenantDomain> {
        return toTenantDomain(
            unwrap(
                tenantDomainResourceSchema,
                await api.post(`/tenant/domains/${domainUuid}/verify`, undefined, {
                    ...WRITE,
                    timeoutMs: 30_000,
                }),
            ),
        );
    },

    /** Maakt dit het hoofddomein voor zijn soort. */
    async makePrimary(domainUuid: string): Promise<TenantDomain> {
        return toTenantDomain(
            unwrap(
                tenantDomainResourceSchema,
                await api.post(`/tenant/domains/${domainUuid}/primary`, undefined, WRITE),
            ),
        );
    },

    /**
     * Start het klaarzetten van een rapportdomein opnieuw: na een stop, of om niet op de
     * volgende DNS-controle te wachten. Niets om opnieuw te starten is een 409 met de reden.
     */
    async retryDomainSetup(domainUuid: string): Promise<TenantDomain> {
        return toTenantDomain(
            unwrap(
                tenantDomainResourceSchema,
                await api.post(`/tenant/domains/${domainUuid}/setup`, undefined, WRITE),
            ),
        );
    },

    async removeDomain(domainUuid: string): Promise<void> {
        await api.delete(`/tenant/domains/${domainUuid}`, { ...WRITE, expect: 'none' });
    },
};
